import {unixTimestampMSFromUTCString} from '@wandb/weave/common/util/time';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Icon} from '@wandb/weave/components/Icon';
import {safeLocalStorage} from '@wandb/weave/core/util/localStorage';
import React, {useEffect, useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {
  NamedWorkspaceUpdateViewFieldsQuery,
  NamedWorkspaceUpdateViewFieldsQueryResult,
  useNamedWorkspaceUpdateViewFieldsQuery,
} from '../../../../generated/graphql';
import {Viewer} from '../../../../state/viewer/types';
import {LoadableView} from '../../../../state/views/types';
import {useViewBarContext} from '../../NamedWorkspaceViewBarContext';
import {parseAndTrackNamedWorkspaces} from '../../utils';
import {formatDate} from '../NamedWorkspaceMenuItem';

const DISPLAY_SAVE_VIEW_MODAL_LOCAL_STORAGE_KEY = 'disabledNwSaveViewModal';
export const getLocalStorageShouldDisableSaveViewModal = () => {
  const val = safeLocalStorage.getItem(
    DISPLAY_SAVE_VIEW_MODAL_LOCAL_STORAGE_KEY
  );
  return val === 'true';
};

const setLocalStorageDisableSaveViewModal = (val: string) => {
  safeLocalStorage.setItem(DISPLAY_SAVE_VIEW_MODAL_LOCAL_STORAGE_KEY, val);
};

export const SaveViewModalContent = () => {
  const {displayName, saveView, currentView, viewer} = useViewBarContext();

  const [shouldDisableModal, setShouldDisableModal] = useState<boolean>(false);
  useEffect(() => {
    const val = getLocalStorageShouldDisableSaveViewModal();
    setShouldDisableModal(val);
  }, []);

  // Go grab the latest updatedValue from db to determine if there is a potential conflict
  const {loading, data: latestData} = useNamedWorkspaceUpdateViewFieldsQuery({
    variables: {id: currentView?.id ?? ''},
    skip: currentView?.id == null,
    fetchPolicy: 'network-only',
  });

  const shouldShowSaveWarning = getShouldShowSaveWarning(
    latestData,
    currentView
  );

  return (
    <Dialog.Portal>
      {/* Force overlay and modal content z-index to be higher than panel bank/panel z-indices in single run workspace */}
      <Dialog.Overlay className="z-[111]" />
      <Dialog.Content
        className={twMerge(
          'fixed z-[112]',
          'w-screen min-w-[18rem] px-16',
          'sm:w-[37.5rem] sm:px-32 sm:py-24',
          'left-[10%] top-[15%] translate-x-[-10%] translate-y-0',
          'sm:left-[50%] sm:top-[33%] sm:translate-x-[-50%]'
        )}
        onOpenAutoFocus={e => {
          e.preventDefault();
        }}
        onCloseAutoFocus={e => {
          e.preventDefault();
        }}>
        <Dialog.Title className="break-all">
          Save changes{displayName.length > 0 ? ` to "${displayName}"` : ''}?
        </Dialog.Title>
        <Dialog.Description className="mb-14 mt-12 text-base">
          This will update the current view, making your changes available to
          all your teammates in this project.
        </Dialog.Description>
        {shouldShowSaveWarning && (
          <div
            className={twMerge(
              'flex items-center gap-16 rounded-lg',
              'mt-16 px-16 py-12',
              'text-sienna-600 dark:text-sienna-300',
              'bg-sienna-300/[.24] dark:bg-sienna-700/[.24]'
            )}>
            <Icon className="shrink-0" name="info" width={18} height={18} />
            <WarningMessage latestView={latestData?.view} />
          </div>
        )}
        {!shouldShowSaveWarning && (
          <div className="mt-16 flex gap-8 text-base leading-[22px]">
            <Checkbox
              id="disable-save-modal"
              checked={shouldDisableModal}
              onCheckedChange={() => setShouldDisableModal(prev => !prev)}
            />
            <label htmlFor="disable-save-modal">
              Do not show this modal next time
            </label>
          </div>
        )}
        <div className="mt-24 flex flex-col gap-8 sm:flex-row">
          <Dialog.Close asChild>
            <Button
              data-test="save-view-modal-button"
              disabled={loading}
              variant="primary"
              className="!filter-none"
              onClick={() => {
                if (shouldDisableModal) {
                  setLocalStorageDisableSaveViewModal('true');
                }
                parseAndTrackNamedWorkspaces({
                  action: 'Save view',
                  location: 'modal',
                  currentView,
                  viewer,
                });
                saveView?.();
              }}>
              Save view
            </Button>
          </Dialog.Close>
          <Dialog.Close asChild>
            <Button variant="ghost" className="!filter-none">
              Cancel
            </Button>
          </Dialog.Close>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );
};

type WarningMessageProps = {
  latestView: NamedWorkspaceUpdateViewFieldsQuery['view'] | undefined;
};
const WarningMessage = ({latestView}: WarningMessageProps) => {
  const {viewer} = useViewBarContext();

  const lastUpdateAt = useMemo(() => getLastUpdateAt(latestView), [latestView]);
  const lastUpdateByDisplayName = useMemo(
    () => getLastUpdatedByDisplayName(latestView, viewer),
    [latestView, viewer]
  );

  if (viewer == null || !viewer.username || !lastUpdateAt) {
    console.error(
      'Invalid state - user must be logged in to save shared views'
    );
  }

  return (
    <p className="text-base text-sienna-600">
      <span className="font-bold">
        This view was last saved {lastUpdateAt && `on ${lastUpdateAt}`}{' '}
        {`by ${lastUpdateByDisplayName}.`}{' '}
      </span>
      Saving your changes now will override these edits.
    </p>
  );
};

const getLastUpdateAt = (latestView: WarningMessageProps['latestView']) => {
  const date = latestView?.updatedAt ?? latestView?.createdAt;
  if (date == null) {
    return undefined;
  }
  return formatDate(date);
};

const getLastUpdateByUsername = (
  latestView: WarningMessageProps['latestView']
) => {
  if (latestView?.updatedBy && latestView?.updatedBy?.username) {
    return latestView.updatedBy.username;
  }
  return latestView?.user?.username ?? undefined;
};
const getLastUpdatedByDisplayName = (
  latestView: WarningMessageProps['latestView'],
  viewer: Viewer | undefined
) => {
  const lastUpdateByUsername = getLastUpdateByUsername(latestView);

  if (lastUpdateByUsername == null) {
    return 'someone';
  }
  if (lastUpdateByUsername === viewer?.username) {
    return 'you';
  }
  return lastUpdateByUsername;
};

export const getShouldShowSaveWarning = (
  lastStoredData: NamedWorkspaceUpdateViewFieldsQueryResult['data'],
  currentView: LoadableView | undefined
) => {
  if (!lastStoredData) {
    return false;
  }

  const lastStoredTimestamp =
    lastStoredData.view?.updatedAt ?? lastStoredData.view?.createdAt;
  const currSaveTimestamp = currentView?.updatedAt ?? currentView?.createdAt;
  if (currSaveTimestamp == null || lastStoredTimestamp == null) {
    return false;
  }

  return (
    unixTimestampMSFromUTCString(lastStoredTimestamp) >
    unixTimestampMSFromUTCString(currSaveTimestamp)
  );
};
